import { useAtom } from "jotai";
import type { ExtractAtomValue } from "jotai";
import { ResponseGetMerchantSettings } from "@CommonApi/merchant-settings";
import {
  featureFlagsAtom,
  merchantSettingsAtom,
  merchantSettingsLoadFailedAtom,
  merchantSettingsLoadedAtom,
  settingsAtom,
} from "./store/atoms";
import { mapMerchantSettingsToReportSettings } from "./store/reportSettings";
import { ReactNode, useEffect } from "react";
import { AppConfigurationClient } from "@azure/app-configuration";
import { FeatureFlag } from "./store/types";
import { useApi } from "src/api";
import { useUser } from "src/atoms/user";
import { env } from "src/utils/env";

interface Props {
  children: ReactNode;
}

/* Built on first use, inside the fallback below: a connection string the client refuses (a local
   stack without one) turns every flag off instead of failing the module load. */
let client: AppConfigurationClient | null = null;
const flagStore = () => (client ??= new AppConfigurationClient(env.VITE_FF_FLAG_STORE));

let loadedFeatureFlags: ExtractAtomValue<typeof featureFlagsAtom> | null = null;
let loadedSettingsMerchantId: number | null = null;
const loadSettings = async ({
  getMerchantSettingsApi,
}: {
  getMerchantSettingsApi: () => Promise<ResponseGetMerchantSettings | null>;
}) => {
  const merchantSettings = await getMerchantSettingsApi();
  if (!merchantSettings) {
    return null;
  }

  return merchantSettings;
};

const loadFeatureFlags = async () => {
  if (loadedFeatureFlags) return loadedFeatureFlags;

  const newFeatureFlags = {} as Record<FeatureFlag, boolean>;
  try {
    const clientFeatureFlags = flagStore().listConfigurationSettings({
      keyFilter: ".appconfig.featureflag*",
    });

    for await (const featureFlag of clientFeatureFlags) {
      try {
        if (featureFlag.value) {
          const featureFlagValue = JSON.parse(featureFlag.value) as {
            id: string;
            enabled: boolean;
          };
          newFeatureFlags[featureFlagValue.id as FeatureFlag] =
            featureFlagValue.enabled &&
            featureFlag.tags !== undefined &&
            featureFlag.tags[env.VITE_ENV] === "true";
        }
      } catch {
        console.error(
          `Failed to parse feature flag ${featureFlag.key} with value ${featureFlag.value}`,
        );
      }
    }
  } catch (error) {
    // The flag store cannot be reached (offline, or a local stack without it): every flag
    // reads off for this page, and the next page load asks again.
    console.warn("Feature flags could not be loaded; every flag is off.", error);
    return {} as Record<FeatureFlag, boolean>;
  }
  loadedFeatureFlags = newFeatureFlags;
  return newFeatureFlags;
};

export const FeatureFlagProvider = ({ children }: Props) => {
  const { getMerchantSettingsApi } = useApi();
  const [featureFlags, setFeatureFlags] = useAtom(featureFlagsAtom);
  const [settings, setSettings] = useAtom(settingsAtom);
  const [merchantSettings, setMerchantSettings] = useAtom(merchantSettingsAtom);
  const [, setMerchantSettingsLoaded] = useAtom(merchantSettingsLoadedAtom);
  const [, setMerchantSettingsLoadFailed] = useAtom(merchantSettingsLoadFailedAtom);
  const { user } = useUser();

  useEffect(() => {
    if (!user) {
      return;
    }

    if (loadedSettingsMerchantId !== user.merchantId) {
      loadedSettingsMerchantId = user.merchantId;
      setSettings(null);
      setMerchantSettings(null);
      setMerchantSettingsLoaded(false);
      setMerchantSettingsLoadFailed(false);
    }

    if (settings == null || merchantSettings == null) {
      let isCurrent = true;
      loadSettings({
        getMerchantSettingsApi,
      }).then((response) => {
        if (isCurrent) {
          if (response) {
            setSettings(mapMerchantSettingsToReportSettings(response));
            setMerchantSettings(response);
            setMerchantSettingsLoadFailed(false);
          } else {
            setMerchantSettingsLoadFailed(true);
          }
          setMerchantSettingsLoaded(true);
        }
      });

      return () => {
        isCurrent = false;
      };
    }
  }, [
    merchantSettings,
    setMerchantSettings,
    setMerchantSettingsLoadFailed,
    setMerchantSettingsLoaded,
    setSettings,
    settings,
    user,
  ]);

  useEffect(() => {
    if (featureFlags === null) {
      loadFeatureFlags().then(setFeatureFlags);
    }
  }, []);

  return <>{children}</>;
};
